<template>
  <div
    class="primary-lighten-1 mb-2 space-y-2 rounded-md p-2"
    :class="{ 'pointer-events-none opacity-50': isDeleting }"
  >
    <div class="flex flex-grow gap-x-2">
      <PFormField label="Machine" sr-only class="flex-grow">
        <InputCombobox
          v-model="modelValue.machine"
          :items="selectableMachines"
          items-key-name="id"
          items-value-name="name"
          creation-option
          :disable-control="$attrs.disabled as boolean"
          :creation-model-type="Machine"
          auto-select-newly-created-model
          @item-selected="onItemSelected"
          @focusout="onFocusOut"
          ref="machineComboboxRef"
        />
      </PFormField>
      <PButton
        :icon="['fas', 'trash-can']"
        variant="ghost"
        class="hover:text-error py-0!"
        @click="deleteRoleServiceProviderMachine"
      />
      <PButton
        :icon="['fas', 'grip-vertical']"
        variant="ghost"
        class="drag-handle cursor-grab! py-0!"
      />
    </div>
    <PFormField label="Standaardin- en verkoopartikel" sr-only>
      <InputCombobox
        v-model="modelValue.purchaseItem"
        :items="purchaseItems"
        items-key-name="id"
        :items-value-function="
          (item) => `${item.getDisplayText()} ${item.saleItem?.getDisplayText()}`
        "
        search-thoroughly
        :disabled="!modelValue.machine"
        input-placeholder="Standaardin- en verkoopartikel"
        :creation-model-type="PurchaseItem"
        @item-selected="updateRoleServiceProviderMachine"
        @item-deselected="updateRoleServiceProviderMachine"
        ref="purchaseItemComboboxRef"
      >
        <template #custom-list-item="{ item, active }">
          <component
            :is="item.saleItem ? ComboboxTransactionGroupDisplay : ComboboxPurchaseItemDisplay"
            :item="item"
            class="block py-2 pl-2"
            :class="{ 'primary cursor-pointer': active }"
          />
        </template>
        <template #custom-selected-item="{ item }">
          <component
            :is="item.saleItem ? ComboboxTransactionGroupDisplay : ComboboxPurchaseItemDisplay"
            :item="item"
          />
        </template>
      </InputCombobox>
    </PFormField>
  </div>
</template>

<script setup lang="ts">
import PFormField from "@/components/form/PFormField.vue"
import { Machine } from "@/models/machine.model.ts"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import PButton from "@/components/elements/PButton.vue"
import { storeToRefs } from "pinia"
import type { RoleServiceProviderMachine } from "@/models/role-service-provider-machine.model.ts"
import { ref } from "vue"
import { usePurchaseItemStore } from "@/store/purchase-items.store.ts"
import ComboboxTransactionGroupDisplay from "@/components/administration/combobox-display-templates/ComboboxTransactionGroupDisplay.vue"
import ComboboxPurchaseItemDisplay from "@/components/administration/combobox-display-templates/ComboboxPurchaseItemDisplay.vue"
import { PurchaseItem } from "@/models/purchase-item.model.ts"

defineProps<{
  selectableMachines: Machine[]
}>()

const emit = defineEmits(["update:modelValue", "delete"])

const purchaseItemStore = usePurchaseItemStore()
const { purchaseItems } = storeToRefs(purchaseItemStore)

const modelValue = defineModel<RoleServiceProviderMachine>({ required: true })

const lastSelectedMachine = ref<Machine | undefined>()

function onItemSelected() {
  lastSelectedMachine.value = modelValue.value.machine
  updateRoleServiceProviderMachine()
}

function onFocusOut() {
  if (!modelValue.value.machine && lastSelectedMachine.value) {
    modelValue.value.machine = lastSelectedMachine.value
  }
}

function updateRoleServiceProviderMachine() {
  emit("update:modelValue", modelValue.value)
}

const isDeleting = ref(false)
function deleteRoleServiceProviderMachine() {
  isDeleting.value = true
  emit("delete", modelValue.value)
}

const machineComboboxRef = ref<typeof InputCombobox>()
function focusMachineCombobox() {
  machineComboboxRef.value!.focus()
}

const purchaseItemComboboxRef = ref<typeof InputCombobox>()
function focusPurchaseItemCombobox() {
  purchaseItemComboboxRef.value!.focus()
}

defineExpose({ focusMachineCombobox, focusPurchaseItemCombobox })
</script>

<style scoped></style>
